import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiTrash2, FiShoppingBag } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { showDeleteConfirm } from '../utils/sweetalert';
import { getProductImage } from '../utils/imageHelpers';
import useCartStore from '../store/useCartStore';
import useAuthStore from '../store/useAuthStore';
import PlaceholderImage from '../components/PlaceholderImage';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../utils/formatPrice';

const Cart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { cart, loading, fetchCart, updateItem, removeItem, clearCart, getTotalPrice } = useCartStore();

  const items = cart?.items || [];
  const validItems = items.filter(item => item.product);

  useEffect(() => {
    fetchCart();
  }, [isAuthenticated]);

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      await updateItem(itemId, newQuantity);
      toast.success(t('cart.messages.quantityUpdated'));
    } catch (error) {
      toast.error(t('cart.messages.updateError'));
    }
  };

  const handleRemove = async (itemId) => {
    const result = await showDeleteConfirm(t('cart.confirmRemove'));
    if (!result.isConfirmed) return;

    try {
      await removeItem(itemId);
      toast.success(t('cart.messages.removed'));
    } catch (error) {
      toast.error(t('cart.messages.removeError'));
    }
  };

  const handleClearCart = async () => {
    const result = await showDeleteConfirm(t('cart.confirmClear'));
    if (!result.isConfirmed) return;

    try {
      await clearCart();
      toast.success(t('cart.messages.cleared'));
    } catch (error) {
      toast.error(t('cart.messages.clearError'));
    }
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (validItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6">
        <FiShoppingBag className="text-6xl text-gray-300 mb-6" />
        <h2 className="font-serif text-3xl font-light tracking-wide text-gray-900 mb-4">
          {t('cart.empty.title')}
        </h2>
        <p className="text-gray-600 font-light tracking-wide mb-8">
          {t('cart.empty.description')}
        </p>
        <Link to="/products" className="btn-primary">
          {t('cart.empty.button')}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-100 py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-serif text-4xl md:text-5xl font-light tracking-wide text-center">
            {t('cart.title')}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Clear Cart Button */}
            <div className="flex justify-end">
              <button
                onClick={handleClearCart}
                className="text-gray-600 hover:text-gray-900 transition text-sm tracking-wider uppercase font-light flex items-center gap-2"
              >
                <FiTrash2 />
                {t('cart.clearCart')}
              </button>
            </div>

            {/* Items List */}
            <div className="space-y-4">
              {validItems.map((item) => {
                const product = item.product;
                if (!product) return null;

                const price = product.salePrice || product.price;

                return (
                  <div key={item._id} className="flex gap-6 py-6 border-b border-gray-100">
                    {/* Product Image */}
                    <Link
                      to={`/products/${product.slug}`}
                      className="flex-shrink-0 w-32 h-32 bg-gray-50 overflow-hidden"
                    >
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={getProductImage(product.images, 0)}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <PlaceholderImage type="product" className="w-full h-full" />
                      )}
                    </Link>

                    {/* Product Info */}
                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <Link
                          to={`/products/${product.slug}`}
                          className="font-light text-gray-900 hover:text-gray-600 transition mb-2 block"
                        >
                          {product.name}
                        </Link>
                        {product.modelNumber && (
                          <p className="text-xs text-gray-500 font-light tracking-wider uppercase">
                            {t('product.model')} {product.modelNumber}
                          </p>
                        )}
                        {item.ringSize && (
                          <p className="text-xs text-gray-500 font-light tracking-wider mt-1">
                            Yüzük Ölçüsü: {item.ringSize}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-4">
                        {/* Quantity Selector */}
                        <div className="flex items-center border border-gray-200">
                          <button
                            onClick={() => handleQuantityChange(item._id, item.quantity - 1)}
                            className="px-3 py-2 hover:bg-gray-50 transition text-sm"
                          >
                            −
                          </button>
                          <span className="px-4 py-2 border-x border-gray-200 min-w-[50px] text-center font-light text-sm">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleQuantityChange(item._id, item.quantity + 1)}
                            className="px-3 py-2 hover:bg-gray-50 transition text-sm"
                            disabled={item.quantity >= product.stock}
                          >
                            +
                          </button>
                        </div>

                        {/* Price */}
                        <span className="font-light text-gray-900">
                          {formatPrice(price * item.quantity)} ₺
                        </span>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemove(item._id)}
                      className="text-gray-400 hover:text-gray-900 transition"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 p-8 sticky top-24">
              <h2 className="font-serif text-2xl font-light tracking-wide mb-8">
                {t('cart.summary.title')}
              </h2>

              <div className="space-y-4 mb-8">
                <div className="flex justify-between font-light">
                  <span className="text-gray-600">{t('cart.summary.subtotal')}</span>
                  <span className="text-gray-900">{formatPrice(getTotalPrice())} ₺</span>
                </div>
                <div className="flex justify-between font-light">
                  <span className="text-gray-600">{t('cart.summary.shipping')}</span>
                  <span className="text-gray-900">{t('cart.summary.freeShipping')}</span>
                </div>
                <div className="border-t border-gray-200 pt-4 flex justify-between">
                  <span className="font-light text-gray-900 text-lg">{t('cart.summary.total')}</span>
                  <span className="font-light text-gray-900 text-2xl">{formatPrice(getTotalPrice())} ₺</span>
                </div>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="btn-primary w-full mb-4"
              >
                {t('cart.checkout')}
              </button>

              <Link
                to="/products"
                className="block text-center text-gray-600 hover:text-gray-900 transition text-sm tracking-wider uppercase font-light"
              >
                {t('cart.continueShopping')}
              </Link>

              {/* Trust Badges */}
              <div className="mt-8 pt-8 border-t border-gray-200 space-y-3">
                <div className="flex items-center gap-3 text-sm font-light text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('cart.benefits.securePayment')}
                </div>
                <div className="flex items-center gap-3 text-sm font-light text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                  </svg>
                  {t('cart.benefits.returnGuarantee')}
                </div>
                <div className="flex items-center gap-3 text-sm font-light text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {t('cart.benefits.fastDelivery')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
